import * as THREE from 'three';

export type TrackingQuality = 'IDEAL' | 'DRIFTING' | 'OFF_TRACK';

export class TrajectoryGuide {
  public group: THREE.Group;
  public isVisible: boolean = true;
  public trackingQuality: TrackingQuality = 'IDEAL';
  public currentDeviation: number = 0;

  private ribbonMesh: THREE.Mesh | null = null;
  private ribbonMat: THREE.MeshBasicMaterial;
  private chevronTexture: THREE.CanvasTexture;
  private currentPathPoints: THREE.Vector3[] = [];
  private animOffset: number = 0;
  private ribbonWidth: number = 1.15; // Ribbon width matching typical German car track width

  constructor() {
    this.group = new THREE.Group();
    this.group.name = 'trajectory_guide';

    this.chevronTexture = this.createChevronTexture();
    this.ribbonMat = new THREE.MeshBasicMaterial({
      map: this.chevronTexture,
      color: 0x22c55e, // Vibrant neon green
      transparent: true,
      opacity: 0.82,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }

  public setVisible(visible: boolean) {
    this.isVisible = visible;
    this.group.visible = visible;
  }

  public toggleVisible(): boolean {
    this.setVisible(!this.isVisible);
    return this.isVisible;
  }

  public getIdealPath(): THREE.Vector3[] {
    return this.currentPathPoints;
  }

  public getDeviation(pos: THREE.Vector3): number {
    if (this.currentPathPoints.length === 0) return 0;
    // Calculate distance to the nearest segment of current trajectory path
    let minDist = Infinity;
    for (let i = 0; i < this.currentPathPoints.length - 1; i++) {
      const p1 = this.currentPathPoints[i];
      const p2 = this.currentPathPoints[i + 1];
      const dist = this.distancePointToSegment(pos, p1, p2);
      if (dist < minDist) {
        minDist = dist;
      }
    }
    return minDist;
  }

  public getTrackingQuality(pos: THREE.Vector3): TrackingQuality {
    const dev = this.getDeviation(pos);
    if (dev <= 0.45) return 'IDEAL';
    if (dev <= 0.90) return 'DRIFTING';
    return 'OFF_TRACK';
  }

  private createChevronTexture(): THREE.CanvasTexture {
    if (typeof document === 'undefined') {
      return new THREE.CanvasTexture({} as HTMLCanvasElement);
    }
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.CanvasTexture(canvas);

    ctx.clearRect(0, 0, 128, 256);

    // Glowing green chevron arrows >>> pointing forward (towards top)
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = '#4ade80';
    ctx.shadowBlur = 8;

    for (let i = 0; i < 2; i++) {
      const yBase = i * 128;
      ctx.beginPath();
      ctx.moveTo(64, yBase + 20);
      ctx.lineTo(112, yBase + 72);
      ctx.lineTo(96, yBase + 82);
      ctx.lineTo(64, yBase + 48);
      ctx.lineTo(32, yBase + 82);
      ctx.lineTo(16, yBase + 72);
      ctx.closePath();
      ctx.fill();

      // Subtle edge line
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1, 4);
    return texture;
  }

  public update(
    deltaTime: number,
    vehiclePos: THREE.Vector3,
    vehicleRotY: number,
    navInstructionId: string = ''
  ) {
    if (!this.isVisible) {
      this.group.visible = false;
      return;
    }
    this.group.visible = true;

    // 1. Animate chevron UVs scrolling forward
    this.animOffset -= deltaTime * 1.8;
    if (this.animOffset < -1.0) this.animOffset += 1.0;
    this.chevronTexture.offset.set(0, this.animOffset);

    // 2. Compute waypoints based on map sector and upcoming turn instruction
    const waypoints = this.calculateIdealWaypoints(vehiclePos, vehicleRotY, navInstructionId);
    this.buildRibbonMesh(waypoints);

    // 3. Compute driver deviation & update color feedback
    this.currentDeviation = this.getDeviation(vehiclePos);
    this.trackingQuality = this.getTrackingQuality(vehiclePos);

    if (this.trackingQuality === 'IDEAL') {
      // Vibrant neon green
      this.ribbonMat.color.setHex(0x22c55e);
      this.ribbonMat.opacity = 0.85;
    } else if (this.trackingQuality === 'DRIFTING') {
      // Warning amber
      this.ribbonMat.color.setHex(0xeab308);
      this.ribbonMat.opacity = 0.90;
    } else {
      // Off track / oncoming danger crimson
      this.ribbonMat.color.setHex(0xef4444);
      this.ribbonMat.opacity = 0.95;
    }
  }

  public calculateIdealWaypoints(
    pos: THREE.Vector3,
    rotY: number,
    navId: string
  ): THREE.Vector3[] {
    const pts: THREE.Vector3[] = [];
    const forwardZ = Math.cos(rotY);
    const forwardX = Math.sin(rotY);
    const yHeight = 0.035; // Floating cleanly above road markings and asphalt

    // Sector 1: Inside Roundabout (takes precedence over crossing axes near origin)
    const rDist = Math.hypot(pos.x, pos.z);
    if (rDist <= 19.5 && rDist >= 10.0) {
      const currAngle = Math.atan2(pos.x, pos.z);
      // Counter-clockwise circulating path along radius ~14.5m
      for (let i = 0; i <= 10; i++) {
        const a = currAngle + (i * 0.18);
        pts.push(new THREE.Vector3(14.5 * Math.sin(a), yHeight, 14.5 * Math.cos(a)));
      }
      return pts;
    }

    // Sector 2: Main Boulevard North-South (|x| < 6.0)
    if (Math.abs(pos.x) < 6.0) {
      const isHeadingNorth = forwardZ >= -0.2;
      const targetLaneX = isHeadingNorth ? -2.0 : 2.0;

      if (isHeadingNorth) {
        // Approaching intersection at z=60
        if (pos.z > 20 && pos.z <= 65) {
          if (navId.includes('right') || navId.includes('goethe') || navId.includes('30_n1_e')) {
            // Turn right onto Goethestr. (x > 0, z ≈ 58.5)
            pts.push(new THREE.Vector3(targetLaneX, yHeight, pos.z));
            pts.push(new THREE.Vector3(targetLaneX, yHeight, 52));
            pts.push(new THREE.Vector3(-1.0, yHeight, 55));
            pts.push(new THREE.Vector3(3.0, yHeight, 58.0));
            pts.push(new THREE.Vector3(8.0, yHeight, 58.5));
            pts.push(new THREE.Vector3(25.0, yHeight, 58.5));
            pts.push(new THREE.Vector3(45.0, yHeight, 58.5));
            return pts;
          } else if (navId.includes('left') || navId.includes('bahnhof')) {
            // Turn left onto Bahnhofstr. (x < 0, z ≈ 61.5)
            pts.push(new THREE.Vector3(targetLaneX, yHeight, pos.z));
            pts.push(new THREE.Vector3(targetLaneX, yHeight, 52));
            pts.push(new THREE.Vector3(targetLaneX, yHeight, 58));
            pts.push(new THREE.Vector3(-3.0, yHeight, 61.0));
            pts.push(new THREE.Vector3(-10.0, yHeight, 61.5));
            pts.push(new THREE.Vector3(-30.0, yHeight, 61.5));
            return pts;
          }
        }

        // Approaching North Ring T-junction at z=120
        if (pos.z > 70 && pos.z <= 122) {
          if (navId.includes('left')) {
            pts.push(new THREE.Vector3(targetLaneX, yHeight, pos.z));
            pts.push(new THREE.Vector3(targetLaneX, yHeight, 110));
            pts.push(new THREE.Vector3(targetLaneX, yHeight, 116));
            pts.push(new THREE.Vector3(-4.0, yHeight, 122.0));
            pts.push(new THREE.Vector3(-25.0, yHeight, 122.0));
            pts.push(new THREE.Vector3(-50.0, yHeight, 122.0));
            return pts;
          } else {
            pts.push(new THREE.Vector3(targetLaneX, yHeight, pos.z));
            pts.push(new THREE.Vector3(targetLaneX, yHeight, 110));
            pts.push(new THREE.Vector3(-0.5, yHeight, 115));
            pts.push(new THREE.Vector3(3.5, yHeight, 118.0));
            pts.push(new THREE.Vector3(25.0, yHeight, 118.0));
            pts.push(new THREE.Vector3(50.0, yHeight, 118.0));
            return pts;
          }
        }

        // General straight northbound
        const aheadDist = 48.0;
        for (let d = 0; d <= aheadDist; d += 6.0) {
          pts.push(new THREE.Vector3(targetLaneX, yHeight, pos.z + d));
        }
        return pts;
      } else {
        // Southbound
        if (pos.z > 18 && pos.z < 60) {
          pts.push(new THREE.Vector3(targetLaneX, yHeight, pos.z));
          pts.push(new THREE.Vector3(targetLaneX, yHeight, 26));
          pts.push(new THREE.Vector3(targetLaneX, yHeight, 20));
          pts.push(new THREE.Vector3(5.5, yHeight, 16.0));
          pts.push(new THREE.Vector3(12.0, yHeight, 10.0));
          pts.push(new THREE.Vector3(14.5, yHeight, 0.0));
          return pts;
        }

        const aheadDist = 48.0;
        for (let d = 0; d <= aheadDist; d += 6.0) {
          pts.push(new THREE.Vector3(targetLaneX, yHeight, pos.z - d));
        }
        return pts;
      }
    }

    // Sector 3: Main Avenue East-West (|z| < 6.0)
    if (Math.abs(pos.z) < 6.0) {
      const isHeadingEast = forwardX >= -0.2;
      const targetLaneZ = isHeadingEast ? -2.0 : 2.0;

      if (!isHeadingEast && pos.x > -65 && pos.x < -18) {
        // Approaching roundabout from West
        pts.push(new THREE.Vector3(pos.x, yHeight, targetLaneZ));
        pts.push(new THREE.Vector3(-24, yHeight, targetLaneZ));
        pts.push(new THREE.Vector3(-18, yHeight, targetLaneZ));
        pts.push(new THREE.Vector3(-14.5, yHeight, -5.0));
        pts.push(new THREE.Vector3(-10.0, yHeight, -12.0));
        pts.push(new THREE.Vector3(0.0, yHeight, -14.5));
        return pts;
      }

      const aheadDist = 48.0;
      for (let d = 0; d <= aheadDist; d += 6.0) {
        pts.push(new THREE.Vector3(pos.x + (isHeadingEast ? d : -d), yHeight, targetLaneZ));
      }
      return pts;
    }

    // Fallback Sector: Follow current vehicle heading projected ahead
    const aheadDist = 42.0;
    for (let d = 0; d <= aheadDist; d += 6.0) {
      pts.push(new THREE.Vector3(pos.x + forwardX * d, yHeight, pos.z + forwardZ * d));
    }
    return pts;
  }

  private buildRibbonMesh(rawWaypoints: THREE.Vector3[]) {
    if (rawWaypoints.length < 2) return;

    this.currentPathPoints = rawWaypoints;

    // Smooth spline interpolation using CatmullRomCurve3
    const curve = new THREE.CatmullRomCurve3(rawWaypoints);
    const numSegments = Math.min(36, Math.max(12, rawWaypoints.length * 5));
    const smoothPoints = curve.getPoints(numSegments);

    const positions: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];

    const halfW = this.ribbonWidth / 2;
    const up = new THREE.Vector3(0, 1, 0);

    for (let i = 0; i <= numSegments; i++) {
      const p = smoothPoints[i];
      const t = curve.getTangent(i / numSegments);
      // Right normal vector
      const normal = new THREE.Vector3().crossVectors(t, up).normalize();

      const left = p.clone().sub(normal.clone().multiplyScalar(halfW));
      const right = p.clone().add(normal.clone().multiplyScalar(halfW));

      positions.push(left.x, left.y, left.z);
      positions.push(right.x, right.y, right.z);

      const v = (i / numSegments) * (numSegments / 4);
      uvs.push(0, v);
      uvs.push(1, v);

      if (i < numSegments) {
        const idx = i * 2;
        indices.push(idx, idx + 1, idx + 2);
        indices.push(idx + 1, idx + 3, idx + 2);
      }
    }

    if (!this.ribbonMesh) {
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
      geom.setIndex(indices);
      geom.computeVertexNormals();

      this.ribbonMesh = new THREE.Mesh(geom, this.ribbonMat);
      this.ribbonMesh.name = 'trajectory_ribbon';
      this.group.add(this.ribbonMesh);
    } else {
      const geom = this.ribbonMesh.geometry;
      geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
      geom.setIndex(indices);
      geom.computeVertexNormals();
      geom.attributes.position.needsUpdate = true;
      geom.attributes.uv.needsUpdate = true;
    }
  }

  private distancePointToSegment(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): number {
    // 2D horizontal planar XZ distance (ignoring vertical chassis suspension height)
    const abX = b.x - a.x;
    const abZ = b.z - a.z;
    const apX = p.x - a.x;
    const apZ = p.z - a.z;
    const lenSq = abX * abX + abZ * abZ;
    if (lenSq === 0) return Math.hypot(apX, apZ);
    let t = (apX * abX + apZ * abZ) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const projX = a.x + t * abX;
    const projZ = a.z + t * abZ;
    return Math.hypot(p.x - projX, p.z - projZ);
  }
}
